import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Upload, Check, AlertCircle, Loader2, CreditCard, DollarSign, Calendar, Mail, ShieldCheck } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useLanguage } from "@/LanguageContext";
import { t } from "@/translations";
import Breadcrumbs from "@/components/Breadcrumbs";
import { productSchema } from "@/utils/schema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VP_LOGO = "https://customer-assets.emergentagent.com/job_82ee4e38-abb0-4bd3-985f-83a63905409f/artifacts/3xn4ya4w_Vallarta%20Pixel.png";

const BuyPixelsPage = () => {
  const { lang } = useLanguage();
  useSEO({
    title: t("seo.buyTitle", lang),
    description: t("seo.buyDesc", lang),
    canonical: "/buy",
    jsonLd: productSchema(),
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Parse blocks from URL
  const prefilledRow = searchParams.get("row");
  const prefilledCol = searchParams.get("col");
  const blocksParam = searchParams.get("blocks");
  
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [formData, setFormData] = useState({
    fullName: "",
    targetUrl: "",
    slogan: "",
    email: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [blocksAvailable, setBlocksAvailable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingBlocks, setCheckingBlocks] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponDiscountPct, setCouponDiscountPct] = useState(10);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [serverTotalPrice, setServerTotalPrice] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralApplied, setReferralApplied] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(5);
  const [referralError, setReferralError] = useState("");

  // Pick up referral code from URL or sessionStorage and auto-validate
  useEffect(() => {
    const urlRef = searchParams.get("ref");
    let code = "";
    if (urlRef) {
      code = urlRef.toUpperCase();
      sessionStorage.setItem("referral_code", code);
      setReferralCode(code);
    } else {
      const stored = sessionStorage.getItem("referral_code");
      if (stored) {
        code = stored;
        setReferralCode(stored);
      }
    }
    // Auto-validate referral code if present and not yet applied
    if (code && !referralApplied) {
      fetch(`${BACKEND_URL}/api/referral/validate/${code}`)
        .then(res => { if (res.ok) return res.json(); throw new Error(); })
        .then(data => {
          setReferralDiscount(data.buyer_discount_pct);
          setReferralApplied(true);
          setReferralError("");
        })
        .catch(() => {});
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse blocks on mount
  useEffect(() => {
    if (blocksParam) {
      try {
        const blocks = JSON.parse(decodeURIComponent(blocksParam));
        setSelectedBlocks(blocks);
      } catch (e) {
        console.error("Failed to parse blocks", e);
      }
    } else if (prefilledRow && prefilledCol) {
      setSelectedBlocks([{ row: parseInt(prefilledRow), col: parseInt(prefilledCol) }]);
    }
  }, [blocksParam, prefilledRow, prefilledCol]);

  // Check block availability
  useEffect(() => {
    const checkBlocks = async () => {
      if (selectedBlocks.length === 0) {
        setBlocksAvailable(null);
        return;
      }

      setCheckingBlocks(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/check-blocks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(selectedBlocks),
        });
        const data = await response.json();
        setBlocksAvailable(data);
        if (data.total_price !== undefined) setServerTotalPrice(data.total_price);
        // If any blocks are unavailable, redirect to grid with a helpful message
        if (!data.all_available && data.unavailable_blocks?.length > 0) {
          const count = data.unavailable_blocks.length;
          toast.error(
            lang === "en"
              ? `${count} block(s) already taken. Please select different blocks on the grid.`
              : `${count} bloque(s) ya ocupado(s). Selecciona otros bloques en la cuadrícula.`
          );
          navigate("/");
          return;
        }
      } catch (error) {
        setBlocksAvailable({ all_available: false, error: lang === "en" ? "Error checking blocks" : "Error al verificar bloques" });
      }
      setCheckingBlocks(false);
    };

    const debounceTimer = setTimeout(checkBlocks, 300);
    return () => clearTimeout(debounceTimer);
  }, [selectedBlocks, navigate, lang]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t("toast.imageTypesOnly", lang));
      return;
    }

    const maxSize = selectedBlocks.length > 1 ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(selectedBlocks.length > 1 ? t("toast.imageTooLarge5", lang) : t("toast.imageTooLarge2", lang));
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Calculate expected image dimensions for multi-block
  const getExpectedDimensions = () => {
    if (selectedBlocks.length <= 1) return null;
    
    const rows = selectedBlocks.map(b => b.row);
    const cols = selectedBlocks.map(b => b.col);
    const width = (Math.max(...cols) - Math.min(...cols) + 1) * 10;
    const height = (Math.max(...rows) - Math.min(...rows) + 1) * 10;
    
    return { width, height };
  };

  const totalAmount = serverTotalPrice !== null ? serverTotalPrice : selectedBlocks.length * 100;
  const discountAmount = couponApplied ? Math.round(totalAmount * (couponDiscountPct / 100) * 100) / 100 : 0;
  const referralDiscountAmount = referralApplied ? Math.round((totalAmount - discountAmount) * (referralDiscount / 100) * 100) / 100 : 0;
  const finalAmount = totalAmount - discountAmount - referralDiscountAmount;

  const handleApplyCoupon = async () => {
    const email = formData.email.trim().toLowerCase();
    const confirm = confirmEmail.trim().toLowerCase();
    if (!couponCode.trim()) {
      setCouponError(t("coupon.invalidCode", lang));
      return;
    }
    if (!email) {
      setCouponError(t("buy.emailRequired", lang));
      return;
    }
    if (!confirm || email !== confirm) {
      setCouponError(lang === "en" ? "Emails do not match. Please confirm your email." : "Los correos no coinciden. Por favor confirma tu correo.");
      return;
    }
    setCouponLoading(true);
    setCouponError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/coupon/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponCode.trim(), email }),
      });
      const data = await res.json();
      if (data.valid) {
        setCouponApplied(true);
        if (data.discount_percent) setCouponDiscountPct(data.discount_percent);
        toast.success(t("coupon.discountApplied", lang));
      } else {
        setCouponError(data.message || t("coupon.invalidCode", lang));
      }
    } catch {
      setCouponError(t("coupon.invalidCode", lang));
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponCode("");
    setCouponError("");
    setConfirmEmail("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreedToTerms) {
      toast.error(t("buy.toastAgreeTerms", lang));
      return;
    }

    if (!formData.fullName.trim()) {
      toast.error(t("buy.toastEnterName", lang));
      return;
    }

    if (!formData.email) {
      toast.error(t("buy.toastEnterEmail", lang));
      return;
    }

    // If coupon is applied, re-validate eligibility before proceeding to Stripe
    if (couponApplied) {
      try {
        const recheck = await fetch(`${BACKEND_URL}/api/coupon/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: couponCode.trim(), email: formData.email.trim() }),
        });
        const recheckData = await recheck.json();
        if (!recheckData.valid) {
          toast.error(recheckData.message || (lang === "en" ? "This email has already used a coupon. Only one coupon per user." : "Este correo ya usó un cupón. Solo un cupón por usuario."));
          handleRemoveCoupon();
          return;
        }
      } catch {
        toast.error(lang === "en" ? "Could not verify coupon. Please try again." : "No se pudo verificar el cupón. Inténtalo de nuevo.");
        return;
      }
    }

    if (selectedBlocks.length > 9) {
      toast.error(t("buy.toastMaxBlocks", lang));
      return;
    }

    setLoading(true);

    try {
      // Create checkout session
      const response = await fetch(`${BACKEND_URL}/api/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          blocks: selectedBlocks,
          target_url: formData.targetUrl,
          slogan: formData.slogan,
          customer_email: formData.email,
          customer_name: formData.fullName.trim(),
          origin_url: window.location.origin,
          auto_renew: autoRenew,
          coupon_code: couponApplied ? couponCode.trim() : "",
          referral_code: referralApplied ? referralCode.trim() : "",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to create checkout");
      }

      const data = await response.json();
      
      // Store image for later upload if provided
      if (imageFile) {
        localStorage.setItem(`pending_image_${data.group_id}`, imagePreview);
      }
      
      // Mark checkout in progress so we can detect abort
      sessionStorage.setItem("stripe_checkout_pending", "true");
      sessionStorage.setItem("stripe_session_id", data.session_id);
      
      // Redirect to Stripe checkout
      toast.success(t("buy.toastRedirecting", lang));
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message || t("buy.toastCheckoutFailed", lang));
    } finally {
      setLoading(false);
    }
  };

  const expectedDimensions = getExpectedDimensions();

  return (
    <Layout>
      {/* Hero */}
      <section className="hero py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-[#0F172A] mb-4">
            {t("buy.title", lang)}
          </h1>
          <p className="font-pixel text-lg text-[#64748B] tracking-wider">
            {t("buy.subtitle", lang)}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ label: t("buy.title", lang) }]} />
          {selectedBlocks.length === 0 ? (
            <div className="card p-8 text-center">
              <AlertCircle className="mx-auto mb-4 text-[#F59E0B]" size={48} />
              <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-2">
                {t("buy.noBlocks", lang)}
              </h2>
              <p className="text-[#64748B] mb-6">
                {t("buy.noBlocksDesc", lang)}
              </p>
              <Link to="/" className="btn-primary">
                {t("buy.goToGrid", lang)}
              </Link>
            </div>
          ) : (
            <div className="card p-8">
              {/* Order Summary */}
              <div className="mb-8 p-4 bg-[#F8FAFC] border-2 border-[#0F172A]">
                <div className="flex items-center gap-3 mb-3">
                  <img src={VP_LOGO} alt="Vallarta Pixel" className="h-10 w-10" data-testid="order-summary-logo" />
                  <h3 className="font-heading font-bold text-lg text-[#0F172A] flex items-center gap-2">
                    <DollarSign className="text-[#14B8A6]" size={20} />
                    {t("buy.orderSummary", lang)}
                  </h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">{t("buy.blocksSelected", lang)}:</span>
                    <span className="font-bold">{selectedBlocks.length}</span>
                  </div>
                  {selectedBlocks.length === 1 ? (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">{t("buy.position", lang)}:</span>
                      <span className="font-bold">
                        Row {selectedBlocks[0].row + 1}, Col {selectedBlocks[0].col + 1}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">{t("buy.area", lang)}:</span>
                      <span className="font-bold">
                        ({Math.min(...selectedBlocks.map(b => b.row)) + 1}, {Math.min(...selectedBlocks.map(b => b.col)) + 1}) to ({Math.max(...selectedBlocks.map(b => b.row)) + 1}, {Math.max(...selectedBlocks.map(b => b.col)) + 1})
                      </span>
                    </div>
                  )}
                  {/* Per-block pricing breakdown */}
                  {blocksAvailable?.results ? (() => {
                    const prices = blocksAvailable.results.map(b => b.price);
                    const allSame = prices.every(p => p === prices[0]);
                    return allSame ? (
                      <div className="flex justify-between">
                        <span className="text-[#64748B]">{t("buy.pricePerBlock", lang)}:</span>
                        <span className="font-bold">${prices[0]}/{lang === "en" ? "year" : "año"}</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-[#64748B] text-xs font-semibold uppercase">{t("buy.perBlockPricing", lang)}</p>
                        {blocksAvailable.results.map((b, i) => (
                          <div key={i} className="flex justify-between pl-2">
                            <span className="text-[#64748B] text-xs">R{b.row + 1}, C{b.col + 1}</span>
                            <span className="font-bold text-xs">${b.price}/year</span>
                          </div>
                        ))}
                      </div>
                    );
                  })() : (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">{t("buy.pricePerBlock", lang)}:</span>
                      <span className="font-bold">$100/{lang === "en" ? "year" : "año"}</span>
                    </div>
                  )}

                  {/* Block coordinates */}
                  <div className="pt-2 border-t border-[#E2E8F0]">
                    <span className="text-[#64748B] text-xs font-semibold uppercase">{t("buy.blockCoordinates", lang)}</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {selectedBlocks.map((b, i) => (
                        <span key={i} className="inline-block px-1.5 py-0.5 bg-[#0F172A]/5 text-[#0F172A] text-xs font-mono" data-testid={`block-coord-${i}`}>
                          R{b.row + 1}C{b.col + 1}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Discount code section */}
                  <div className="pt-2 border-t border-[#E2E8F0]">
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1 block">
                      {t("coupon.discountCode", lang)}
                    </label>
                    {couponApplied ? (
                      <div className="flex items-center justify-between bg-[#14B8A6]/10 px-3 py-2 border border-[#14B8A6]">
                        <span className="text-sm text-[#14B8A6] font-bold flex items-center gap-1">
                          <Check size={14} /> {couponCode.toUpperCase()} ({t("coupon.applied", lang)})
                        </span>
                        <button type="button" onClick={handleRemoveCoupon} className="text-xs text-[#FF6B6B] font-semibold hover:underline" data-testid="remove-coupon-btn">
                          {t("coupon.remove", lang)}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => { setCouponCode(e.target.value); setCouponError(""); }}
                          placeholder={t("coupon.codePlaceholder", lang)}
                          className="w-full px-3 py-2 border-2 border-[#E2E8F0] text-sm focus:border-[#14B8A6] outline-none"
                          data-testid="coupon-code-input"
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                          className="w-full px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors disabled:opacity-50"
                          data-testid="apply-coupon-btn"
                        >
                          {t("coupon.apply", lang)}
                        </button>
                      </div>
                    )}
                    {couponError && (
                      <p className="text-xs text-[#FF6B6B] mt-1" data-testid="coupon-error">{couponError}</p>
                    )}
                  </div>

                  {couponApplied && (
                    <div className="flex justify-between text-[#14B8A6]">
                      <span>{t("coupon.discount", lang)} ({couponDiscountPct}%):</span>
                      <span className="font-bold">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}

                  {/* Referral Code */}
                  <div className="pt-2 border-t border-[#E2E8F0]">
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1 block">
                      {lang === "en" ? "Referral Code" : "Codigo de Referido"}
                    </label>
                    {referralApplied ? (
                      <div className="flex items-center justify-between bg-[#8B5CF6]/10 px-3 py-2 border border-[#8B5CF6]">
                        <span className="text-sm text-[#8B5CF6] font-bold flex items-center gap-1">
                          <Check size={14} /> {referralCode} ({referralDiscount}% off)
                        </span>
                        <button type="button" onClick={() => { setReferralApplied(false); setReferralError(""); }} className="text-xs text-[#FF6B6B] font-semibold hover:underline" data-testid="remove-referral-btn">
                          {lang === "en" ? "Remove" : "Quitar"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <input
                          type="text"
                          value={referralCode}
                          onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralError(""); }}
                          placeholder={lang === "en" ? "Enter referral code" : "Ingresa código de referido"}
                          className="flex-1 px-3 py-2 border-2 border-[#E2E8F0] text-sm focus:border-[#8B5CF6] outline-none"
                          data-testid="referral-code-input"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            if (!referralCode.trim()) return;
                            try {
                              const res = await fetch(`${BACKEND_URL}/api/referral/validate/${referralCode.trim()}`);
                              if (!res.ok) { setReferralError(lang === "en" ? "Invalid referral code" : "Código de referido inválido"); return; }
                              const data = await res.json();
                              setReferralDiscount(data.buyer_discount_pct);
                              setReferralApplied(true);
                              setReferralError("");
                              toast.success(lang === "en" ? `Referral code applied! ${data.buyer_discount_pct}% discount` : `Código aplicado! ${data.buyer_discount_pct}% de descuento`);
                            } catch { setReferralError(lang === "en" ? "Could not verify code" : "No se pudo verificar"); }
                          }}
                          className="w-full px-4 py-2 bg-[#8B5CF6] text-white text-sm font-semibold hover:bg-[#7C3AED] transition-colors"
                          data-testid="apply-referral-btn"
                        >
                          {lang === "en" ? "Apply" : "Aplicar"}
                        </button>
                      </div>
                    )}
                    {referralError && <p className="text-xs text-[#FF6B6B] mt-1" data-testid="referral-error">{referralError}</p>}
                  </div>

                  {referralApplied && (
                    <div className="flex justify-between text-[#8B5CF6]">
                      <span>{lang === "en" ? "Referral Discount" : "Descuento Referido"} ({referralDiscount}%):</span>
                      <span className="font-bold">-${referralDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-[#E2E8F0] flex justify-between">
                    <span className="font-bold text-[#0F172A]">{t("buy.total", lang)}:</span>
                    <span className="font-bold text-[#14B8A6] text-lg">
                      {(couponApplied || referralApplied) && <span className="line-through text-[#94A3B8] text-sm mr-2">${totalAmount}</span>}
                      ${finalAmount.toFixed(2)}/{lang === "en" ? "year" : "año"}
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-[#64748B]">
                  <Calendar size={14} />
                  <span>{t("buy.subscriptionValid", lang)}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-[#64748B]">
                  <Mail size={14} />
                  <span>{t("buy.supportLabel", lang)} <a href="mailto:ddarious@proton.me" className="text-[#14B8A6] hover:underline" data-testid="contact-support-link">ddarious@proton.me</a></span>
                </div>
              </div>

              {/* Availability Status — only show positive confirmation or loading */}
              {blocksAvailable && (
                <div 
                  className="mb-6 p-4 border-2 border-[#14B8A6] bg-[#14B8A6]/10"
                  data-testid="blocks-status"
                >
                  <div className="flex items-center gap-2">
                    {checkingBlocks ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <Check className="text-[#14B8A6]" size={20} />
                    )}
                    <span className="text-[#14B8A6]">
                      {checkingBlocks 
                        ? t("buy.checking", lang)
                        : t("buy.allAvailable", lang)}
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="buy-pixels-form">
                {/* Full Name */}
                <div className="form-group">
                  <label className="form-label">{t("buy.fullNameLabel", lang)} *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder={t("buy.fullNamePlaceholder", lang)}
                    className="input-field"
                    required
                    data-testid="input-fullname"
                  />
                </div>

                {/* Email */}
                <div className="form-group">
                  <label className="form-label">{t("buy.emailLabel", lang)} *</label>
                  <p className="text-sm text-[#64748B] mb-2">
                    {t("buy.emailHint", lang)}
                  </p>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange(e);
                      // If email changes after coupon was applied, revoke the coupon
                      if (couponApplied) {
                        handleRemoveCoupon();
                        toast.info(lang === "en" ? "Coupon removed — email changed. Please re-apply." : "Cupón eliminado — correo cambiado. Vuelve a aplicar.");
                      }
                    }}
                    placeholder="your@email.com"
                    className="input-field"
                    required
                    data-testid="input-email"
                  />
                </div>

                {/* Confirm Email — shown only when coupon code is being entered or applied */}
                {(couponCode.trim() || couponApplied) && (
                  <div className="form-group">
                    <label className="form-label">
                      {lang === "en" ? "Confirm Email" : "Confirmar Correo"} *
                    </label>
                    <input
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      placeholder={lang === "en" ? "Re-enter your email" : "Reingresa tu correo"}
                      className={`input-field ${couponApplied && confirmEmail && confirmEmail.trim().toLowerCase() !== formData.email.trim().toLowerCase() ? "border-[#FF6B6B]" : ""}`}
                      required={couponApplied}
                      data-testid="input-confirm-email"
                    />
                    {confirmEmail && confirmEmail.trim().toLowerCase() !== formData.email.trim().toLowerCase() && (
                      <p className="text-xs text-[#FF6B6B] mt-1" data-testid="email-mismatch-error">
                        {lang === "en" ? "Emails do not match" : "Los correos no coinciden"}
                      </p>
                    )}
                  </div>
                )}

                {/* Website URL */}
                <div className="form-group">
                  <label className="form-label">{t("buy.urlLabel", lang)} *</label>
                  <input
                    type="url"
                    name="targetUrl"
                    value={formData.targetUrl}
                    onChange={handleInputChange}
                    placeholder="https://yourwebsite.com"
                    className="input-field"
                    required
                    data-testid="input-url"
                  />
                </div>

                {/* Slogan */}
                <div className="form-group">
                  <label className="form-label">Slogan (max 50 characters) *</label>
                  <input
                    type="text"
                    name="slogan"
                    value={formData.slogan}
                    onChange={handleInputChange}
                    maxLength={50}
                    placeholder="Your catchy slogan or description"
                    className="input-field"
                    required
                    data-testid="input-slogan"
                  />
                  <p className="text-sm text-[#64748B] mt-1">
                    {formData.slogan.length}/50 characters
                  </p>
                </div>

                {/* Image Upload - Optional */}
                <div className="form-group">
                  <label className="form-label">{t("buy.imageLabel", lang)}</label>
                  <p className="text-sm text-[#64748B] mb-2">
                    {selectedBlocks.length > 1 
                      ? `${lang === "en" ? "For" : "Para"} ${selectedBlocks.length} ${t("buy.forBlocksUpload", lang)} ${expectedDimensions?.width}x${expectedDimensions?.height}px ${t("buy.orWeResize", lang)}`
                      : t("buy.imageHint", lang)
                    }
                  </p>
                  <div className="flex items-start gap-6">
                    <div 
                      className="image-preview cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="image-preview"
                    >
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" />
                      ) : (
                        <Upload className="text-[#64748B]" size={32} />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        onChange={handleImageChange}
                        className="hidden"
                        data-testid="input-image"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn-secondary w-full"
                      >
                        {imageFile ? t("buy.changeImage", lang) : t("buy.chooseImage", lang)}
                      </button>
                      {imageFile && (
                        <p className="text-sm text-[#64748B] mt-2 truncate">
                          {imageFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Terms Checkbox */}
                <div className="form-group">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-5 h-5 border-2 border-[#0F172A]"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-[#64748B]">
                      {t("buy.termsAgree", lang)}{" "}
                      <Link 
                        to="/terms" 
                        className="text-[#14B8A6] hover:underline"
                        target="_blank"
                      >
                        {t("buy.termsLink", lang)}
                      </Link>
                      {" "}{t("buy.termsUnderstand", lang)}
                    </span>
                  </label>
                </div>

                {/* Auto-Renew Checkbox */}
                <div className="form-group">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoRenew}
                      onChange={(e) => setAutoRenew(e.target.checked)}
                      className="mt-1 w-5 h-5 border-2 border-[#0F172A]"
                      data-testid="auto-renew-checkbox"
                    />
                    <span className="text-[#64748B]">
                      {t("buy.autoRenew", lang)}
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Creating checkout...
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} />
                      Pay ${finalAmount.toFixed(2)} with Stripe
                    </>
                  )}
                </button>

                <p className="text-xs text-[#94A3B8] text-center mt-2" data-testid="mxn-note">
                  {lang === "en"
                    ? "You will be charged in Mexican Pesos (MXN) at the current exchange rate. Your card may apply conversion fees."
                    : "Se cobrara en Pesos Mexicanos (MXN) al tipo de cambio actual. Tu tarjeta puede aplicar comisiones de conversion."}
                </p>

                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#64748B]">
                  <svg viewBox="0 0 32 32" className="w-6 h-6" fill="currentColor">
                    <path d="M32 16c0-8.8-7.2-16-16-16S0 7.2 0 16s7.2 16 16 16 16-7.2 16-16zm-16.8 6.8c-3.4 0-5.2-1.6-6.4-3.6l2-1.2c.8 1.4 2 2.4 4.4 2.4 2 0 3.2-.8 3.2-2.2 0-1.4-1-2-3.2-2.8l-1.2-.4c-2.8-1-4.4-2.2-4.4-4.8 0-2.4 2-4.2 5-4.2 2.4 0 4 .8 5.2 2.8l-2 1.2c-.6-1.2-1.6-1.8-3.2-1.8-1.4 0-2.4.6-2.4 1.8 0 1.2.8 1.8 2.8 2.4l1.2.4c3.2 1.2 4.6 2.4 4.6 5.2.2 2.8-2.2 4.8-5.6 4.8z"/>
                  </svg>
                  <span>{t("buy.securePayment", lang)}</span>
                </div>

                {/* Trust badge — image durability */}
                <div
                  className="mt-3 flex items-center justify-center gap-2 text-xs text-[#64748B]"
                  data-testid="logo-protection-badge"
                  title={lang === "en"
                    ? "Your uploaded logo lives on durable, redundant cloud storage — never lost on redeploys."
                    : "Tu logo vive en almacenamiento en la nube duradero y redundante — nunca se pierde en re-despliegues."}
                >
                  <ShieldCheck size={14} className="text-[#14B8A6]" />
                  <span>
                    {lang === "en"
                      ? "Logos protected — uploaded files stored on persistent CDN"
                      : "Logos protegidos — archivos en CDN persistente"}
                  </span>
                  <Check size={12} className="text-[#14B8A6]" />
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default BuyPixelsPage;
